import { Config } from 'config';

import { forEach, filter, invokeMap, indexOf, includes, reduce,
         head, last, take, uniqueId } from 'lodash';

import { Base, OwnedObject, isArray, concat, clone, cloneDeep, Magnitude,
         whenAll, resolve, reject, map$, find$, findLast$ } from 'gear';

import { Xml, Metadata } from '../util';

import { Brush } from '../style';

import { Bounds } from '../geometry';

import { Group, Figures, autoCreateMultiPartText$, updateArray$ } from '../figure';

import { Layers } from './layer';
import { Page } from './page';
import { Template } from './template';

export const DocumentBackground = OwnedObject.extend({ typeName: 'DocumentBackground',

  Properties: {
    brush : { type: 'Brush', def: Brush.empty, onChange() {} },
    id    : { type: 'string', def: null }
  }
});

function overlapsWith( bounds ) {
  return function(figure) {
    return figure.bounds$() .then( figureBounds => Bounds.areOverlapping(figureBounds,bounds) );
  };
}


function canVisualBackOne$( array, figures ) {
  var from = 0;
  return find$( figures, figure => {
    const i = array.indexOf(figure);
    if( i !== -1 ) {
      return figure.bounds$() .then( bounds => {
      return find$( array.slice(from,i), overlapsWith(bounds) ) .then( overlappingFigureBelow => {
      from = i;
      return !! overlappingFigureBelow;
      }); });
    }
    return resolve(false);
  }) .then( figure => !! figure );
}

function visualBackOne$( array, figures, from = 0 ) {

  if( figures.length === 0 ) {
    return resolve();
  }

  let k = 0;
  const figure = figures[k];
  const i = array.indexOf(figure);
  if( i !== -1 ) {

    return figure.bounds$() .then( bounds => {
    return findLast$( array.slice(from,i), overlapsWith(bounds) ) .then( overlappingFigureBelow => {

    if( overlappingFigureBelow ) {
      const range = [ figure ];
      while( k < figures.length-1 ) {
        const nextFigure = figures[k+1];
        if( array.indexOf( nextFigure ) === i-1 ) {
          range.push( nextFigure );
          ++k;
        }
        else {
          break;
        }
      }
      array.remove(range);
      from = array.indexOf(overlappingFigureBelow);
      array.insert(from,range);
    }
    return visualBackOne$(array,figures.slice(k+1,figures.length),from);
    }); });
  }
  else {
    return visualBackOne$(array,figures.slice(1,figures.length),from);
  }

}



function canVisualForwardOne$( array, figures ) {
  var until = array.length;
  return findLast$( figures, figure => {
    const i = array.indexOf(figure);
    if( i !== -1 ) {
      return figure.bounds$() .then( bounds => {
      return find$( array.slice(i+1,until), overlapsWith(bounds) ) .then( overlappingFigureAbove => {
      until = i;
      return !! overlappingFigureAbove;
      }); });
    }
    return resolve(false);
  }) .then( figure => !! figure );
}

function visualForwardOne$( array, figures, until = array.length ) {

  if( figures.length === 0 ) {
    return resolve();
  }

  let k = figures.length-1;
  const figure = figures[k];
  const i = array.indexOf(figure);
  if( i !== -1 ) {

    return figure.bounds$() .then( bounds => {
    return find$( array.slice(i+1,until), overlapsWith(bounds) ) .then( overlappingFigureAbove => {

    if( overlappingFigureAbove ) {
      const range = [ figure ];
      while( k > 0 ) {
        const nextFigure = figures[k-1];
        if( array.indexOf( nextFigure ) === i-1 ) {
          range.unshift( nextFigure );
          --k;
        }
        else {
          break;
        }
      }
      array.remove(range);
      until = array.indexOf(overlappingFigureAbove);
      array.insert(until+1,range);
    }
    return visualForwardOne$(array,figures.slice(0,k),until);
    }); });
  }
  else {
    return visualForwardOne$(array,figures.slice(0,figures.length-1),until);
  }
}


export const DocumentTag = Base.extend({ typeName: 'DocumentTag',

  Properties : {
    id          : { type: 'string' , def: null  },
    name        : { type: 'string' , def: ''    },
    shared      : { type: 'boolean', def: false },
    owned       : { type: 'boolean', def: false },
    archived    : { type: 'boolean', def: false },
    type        : { type: 'string' , def: null  }
  }
});


export const documentEvents = [

  'load',

  'saved',
  'propertiesupdated',

  'lock',
  'unlock',

  'preview', // [TODO] 'previewstarted' ?

  'render',

  'commitstarted',
  'commitrun',
  'commit',

  'revert',

  'renderstarted',
  'rendercomplete',
  'renderfailed',

  'undo',
  'redo',
  'commitcontent',
  'historycommitstarted',

  'figureschanged',
  'layerschanged',
  'pageschanged',

  'activelayerchanged',
  'activepagechanged',

  // Used only in Doc3D
  'materialschanged',
  'model3dchanged',
  'modeldata3dchanged'
];


export const Document = Base.extend({ typeName: 'Document', __debugProps: true,

  Properties : {
    id          : { type: 'string' , def: null  , manualParsing: true },
    name        : { type: 'string' , def: ''    , manualParsing: true },
    class1      : { type: 'string' , def: null  , manualParsing: true },
    class2      : { type: 'string' , def: null  , manualParsing: true },
    class3      : { type: 'string' , def: null  , manualParsing: true },
    notes       : { type: 'string' , def: ''    , manualParsing: true },
    category    : { type: 'string' , def: null  , manualParsing: true },
    owned       : { type: 'boolean', def: true  , manualParsing: true },
    archived    : { type: 'boolean', def: false , manualParsing: true },
    modified    : { type: 'string' , def: null  , manualParsing: true },
    docType     : { type: 'string' , def: 'Doc' , manualParsing: true },
    externalId  : { type: 'string' , def: null  , manualParsing: true },
    metrics     : { type: 'string' , def: 'mm'  , manualParsing: true },
    xType       : { type: 'string' , def: null  },
    undoLimit   : { type: 'integer', def: 10 , manualParsing: true },
    background  : { type: 'DocumentBackground', def: new DocumentBackground(), manualParsing: true },
    pages       : { type: 'Page[]', def: [],
                    onChange( added,removed) {
                      this.lock();
                      this.fireEvent('pageschanged',this,added,removed);
                      if( ! includes(this._pages,this._activePage) ) {
                        this.activePage = this._pages[0];
                      }
                    },
                    manualParsing: true
                  },
    activePage  : { type: 'Page', def: null, owned: false,
                    get() {
                      if( ! this._activePage ) {
                        this.activePage = this._pages[0];
                      }
                      return this._activePage;
                    },
                    set( page ) {
                      if( this._activePage !== page ) {
                        this._activePage = page;
                        this._activePageChanged = true;
                        this.fireEvent('activepagechanged',this,page);
                      }
                    },
                    manualParsing: true,
                    manualCloning: true
                  },
    tags        : { type: 'DocumentTag[]', def: [], manualParsing:true },
    metadata    : { type: 'Metadata', def: new Metadata({}), manualParsing: true }
  },

  get isDocument() { return true; },

  get activePageIndex() {
    return indexOf(this._pages,this.activePage);
  },
  set activePageIndex( i ) {
    this.activePage = this._pages[i];
  },

  get activePageFigures() {
    return this.activePage.figures;
  },
  get activePageLayers() {
    return this.activePage.layers;
  },

  get activeLayer() {
    return this.activePage.activeLayer;
  },
  set activeLayer( layer ) {
    this.activePage.activeLayer = layer;
  },

  get activeLayerIndex() {
    return this.activePage.activeLayerIndex;
  },
  set activeLayerIndex( i ) {
    this.activePage.activeLayerIndex = i;
  },

  get activeLayerFigures() {
    return this.activeLayer.figures;
  },

  get layers() {
    return this.activePage.layers;
  },

  get figures() {
    let r = [], pages = this._pages;
    for( let k = 0; k < pages.length; ++k ) {
      r = r.concat(pages[k].figures);
    }
    return r;
  },
  set figures( figures_ ) {
    this.releasePages();
    this.activeLayer.figures = figures_;
  },

  release( fs ) {
    return fs===undefined ?
      this.activeLayerFigures.clear() :
      this.activeLayerFigures.remove(fs);
  },

  releaseFigures() {
    const { figures } = this;
    this.remove(figures);
    return figures;
  },

  releaseLayers() {
    return this.activePage._layers.clear();
  },

  releasePages() {
    const { pages } = this;
    const releasedPages = pages.toArray();
    pages.set( new Page() );
    return releasedPages;
  },


  backOne( figures ) {
    this.activeLayerFigures.moveBackward(figures,1);
  },
  toBack( figures ) {
    const { activeLayerFigures } = this;
    activeLayerFigures.moveBackward(figures,activeLayerFigures.length);
  },

  forwardOne( figures ) {
    this.activeLayerFigures.moveForward(figures,1);
  },
  toFront( figures ) {
    const { activeLayerFigures } = this;
    activeLayerFigures.moveForward(figures,activeLayerFigures.length);
  },


  canVisualForwardOne$( figures ) {
    return canVisualForwardOne$( this.activeLayerFigures, figures );
  },
  visualForwardOne$( figures ) {
    return visualForwardOne$( this.activeLayerFigures, figures );
  },

  visualBackOne$( figures ) {
    return visualBackOne$( this.activeLayerFigures, figures );
  },
  canVisualBackOne$( figures ) {
    return canVisualBackOne$( this.activeLayerFigures, figures );
  },


  lock( originator ) {
    if( ! this._lock && ! this.previewing ) {
      this._lock = true;
      this.fireEvent('lock',this,originator);
    }
  },
  unlock( originator ) {
    if( this._lock ){
      this._lock = false;
      this.fireEvent('unlock',this,originator);
    }
  },
  changing() {
    return this._lock;
  },

  _update$( type ) {
    return updateArray$(this._pages,type);
  },

  update$( type ) {
    return this._update$(type) .then( () => this );
  },

  cache$() {
    return this.update$('cache');
  },

  get previewing() {
    return this._previewing;
  },

  fireRenderEvents( type ) {
    if( type === 'preview' ) {
      this._previewing = true;
    }
    else if( type === 'commit' ) {
      this._previewing = false;
    }

    this.fireEvent('rendercomplete',this,type);
    this.fireEvent(type,this);

    this._activePageChanged = false;
    this._activeLayerChanged = false;
  },

  updateAndRender$( type ) {
    this.fireEvent('renderstarted',this,type);
    return this._update$(type) .then( () => {
    this.fireRenderEvents(type);
    return this;
    });
  },

  render$() {
    return this.updateAndRender$('render');
  },

  preview$() {
    this.unlock();
    return this.updateAndRender$('preview');
  },

  commit$() {
    this._isSaved = false;
    const waitList = [];
    this.fireEvent('commitstarted',this,waitList);
    return whenAll(waitList) .then( () => {

    this.fireEvent('commitrun',this);
    this.markUndo();
    if(this._dropPreviousHistoryOnNextCommit) {
      this._dropPreviousHistoryOnNextCommit = false;
      this.dropPreviousHistory();
    }
    this.unlock(); // [CHECK]
    return this.updateAndRender$('commit');
    });
  },

  revert$() {
    return this._applyUndoRedo(this.history[this._currentHistoryPosition],'revert');
  },

  prepareToDraw$( zoom ) {
    return map$( this._pages, page => page.prepareToDraw$(zoom) );
  },

  clone() {
    const api = this.activePageIndex;
    const c = new this.constructor(this);
    c.id = null;
    c._activePage = c._pages[api];
    return c;
  },

  toXmlString() {
    const { appendElement } = Xml;
    const docXml = Xml.fromString('<Cdl></Cdl>');
    const root = docXml.documentElement;
    if ( this._metrics && this._metrics !== 'mm') {
      const lMetricsNode = appendElement(root, 'Metrics');
      lMetricsNode.setAttribute('Unit', this._metrics) 
    }
    const background = this._background,
          background_id = background._id,
          background_brush = background._brush,
          emptyBackground = background_brush._empty && ! background_id;
    const layers = this.activePageLayers;
    const { pages } = this;
    // root.setAttribute('xmlns', 'http://www.vectorx.com/2007/Cdl');
    if(  pages.length === 1 &&  pages[0].isDefault() &&
        layers.length === 1 && layers[0].isDefault() &&
        emptyBackground && this._metadata.isEmpty ) {
        Xml.addObjectArray(root,layers[0]._figures);
    }
    else {
      const docNode = appendElement(root,'Document') ;
      Xml.setupProperties(docNode,this);
      this._metadata.setupXml(docNode);
      if( ! emptyBackground ) {
        const backgroundNode = appendElement(docNode,'Background');
        background_brush.buildXml(backgroundNode);
        if( background_id ) {
          backgroundNode.setAttribute('Id',background_id);
        }
      }
      if( pages.length === 1 && pages[0].isDefault() ) {
        invokeMap(layers,'buildXml',appendElement(docNode,'Layers'));
      }
      else {
        invokeMap(pages,'buildXml',appendElement(docNode,'Pages'));
      }
      this.extraSetupProperties(docNode);
    }
    return Xml.toString(docXml);
  },

  extraSetupProperties(/* docNode */) {

  },

  updateProperties( p ) { // [TODO] Create updateProperties for these?
    if( p ) {
      if( p.name     ) { this.name = p.name; }
      if( p.class1   ) { this.class1 = p.class1; }
      if( p.class2   ) { this.class2 = p.class2; }
      if( p.class3   ) { this.class3 = p.class3; }
      if( p.modified ) { this.modified = p.modified; }
      if( p.notes    ) { this.notes = p.notes; }
      if( p.category ) { this.category = p.category; }
      this.fireEvent('propertiesupdated', this);
    }
  },

  prepareToSave$() {
    return this.commitAllContent$();
  },

  saveCopy$( attrs = {} ) {
    if( ! attrs.copy ) {
      attrs.copy = true;
    }
    return this.save$(attrs);
  },

  // Example: { label: '', notes: '', category: '', docType: '', isCuttable: true }
  save$( attrs = {} ) {

    const _this = this;
    return _this.prepareToSave$() .then( () => {

    // Automatically create a new document if the user doesn't own it
    if( ! _this._owned || attrs.copy === true ) {
      _this.id = null;
    }

    if( attrs.name       ) { _this.name  = attrs.name; }
    if( attrs.class1     ) { _this.class1 = attrs.class1; }
    if( attrs.class2     ) { _this.class2 = attrs.class2; }
    if( attrs.class3     ) { _this.class3 = attrs.class3; }
    if( attrs.modified   ) { _this.modified = attrs.modified; }
    if( attrs.notes      ) { _this.notes = attrs.notes; }
    if( attrs.category   ) { _this.category = attrs.category; }
    if( attrs.docType    ) { _this.docType = attrs.docType; }
    if( attrs.externalId ) { _this.externalId = attrs.externalId; }
    if( attrs.metrics    ) { _this.metrics = attrs.metrics; }

    const manager = this._resources || Config.GlobalResourceManager;
    return manager.saveDocument$(this) .then( () => {

    _this._isSaved = true;
    _this.fireEvent('saved', _this);
    return _this;

    }); });
  },

  pushMarkUndoDisabled() {
    ++this.disableMarkUndo;
  },
  popMarkUndoDisabled() {
    if( this.disableMarkUndo > 0 ) {
      --this.disableMarkUndo;
    }
  },

  markUndo() { // [TODO] Rename
    if( Config.enableDocumentHistory && this.disableMarkUndo===0 && this._undoLimit > 0 ) {
      while( this._currentHistoryPosition > this._undoLimit ) {
        this.history.shift();
        --this._currentHistoryPosition;
      }
      const stateData = this._pagesState();
      ++this._currentHistoryPosition;
      this.history = take( this.history, this._currentHistoryPosition );
      this.history.push( stateData );
    }
  },
  canUndo() { return this._currentHistoryPosition > 0; },
  undo() {
    if( this.canUndo() ) {
      this._currentHistoryPosition--;
      return this._applyUndoRedo(this.history[this._currentHistoryPosition],'undo');
    }
    else {
      return reject();
    }
  },
  canRedo() { return this._currentHistoryPosition < this.history.length-1; },
  redo() {
    if( this.canRedo() ) {
      ++this._currentHistoryPosition;
      return this._applyUndoRedo(this.history[this._currentHistoryPosition],'redo');
    }
    else {
      return reject();
    }
  },

  dropPreviousHistory() {
    this.history.splice(0,this._currentHistoryPosition);
    this._currentHistoryPosition=0;
  },

  dropPreviousHistoryOnNextCommit() {
    this._dropPreviousHistoryOnNextCommit = true;
  },

  // Template interface

  addClipart$( clipart ) {
    return Template(this).addClipart$(clipart);
  },
  replaceClipart$( old, clipart ) {
    return Template(this).replaceClipart$(old,clipart);
  },

  autoCreateMultiPartText$( figures ) {
    return autoCreateMultiPartText$(figures) .then( multiPartText => {
    this.replace(figures,multiPartText);
    return this;
    });
  },

  breakMultiPartText( text ) {
    const parts = text.breakIntoParts();
    this.replace(text,parts);
    return this;
  },

  group( figures ) {
    const group = Group.create(figures);
    this.replace(figures,group);
    return this;
  },

  ungroup( figures ) {
    if( ! isArray(figures) ) {
      figures = [ figures ];
    }
    for(let k = 0, kEnd = figures.length; k < kEnd; ++k) {
      const group = figures[k];
      const parts = group.ungroupedFigures;
      this.replace(group,parts);
    }
    return this;
  },

  extractPowerClipContents( figure ) {
    this.lock();
    this.add( figure.extractPowerClipContents() );
    return this;
  },

  addPowerClipContents( figure, content ) {
    this.lock();
    figure.addPowerClipContents( this.remove(content) );
    return this;
  },

  fixPowerClipContents() {
    this.lock();
    for(let k = 0, kEnd = this.figures.length; k < kEnd; ++k) {
      const figure = this.figures[k];
      figure.deepForEachFigure(this._fixFigurePowerClipContents);
    }
  },
  
  _fixFigurePowerClipContents( figure ) {
    const pccs = figure._powerClipContents;
    if (pccs != null && pccs.length == 1)
    {
      if (figure._properties._powerClipId && figure._properties._powerClipId != "")
      {
        var id = uniqueId("PC");
        pccs[0]._id = id;
        figure._properties._powerClipId = id;
      }
    }
  },

  spawnContentLayersFor( figure, point ) {
    this.activePage.spawnContentLayersFor(figure,point);
  },
  editingContent() {
    return this.activePage.editingContent();
  },
  _commitContent$() {
    return this.activePage.commitContent$() .then( figure => {
    this.fireEvent('commitcontent',this);
    return figure;
    });
  },
  commitContent$() {
    return this._commitContent$() .then(this.commit$);
  },

  _commitAllContent$() {
    const _this = this;
    return _this.editingContent() ? _this._commitContent$() .then( () => _this._commitAllContent$() ) : resolve();
  },
  commitAllContent$() {
    const _this = this;
    const editingContent = _this.editingContent();
    return _this._commitAllContent$() .then( () => editingContent ? _this.commit$() : null );
  },

  reshape$( figures, action ) {
    this.lock();
    return Figures.reshape$(figures, action) .then( newFigures => {
    const replaceFigures = action === 'frontMinusBack' ? last(figures)
                         : action === 'backMinusFront' ? head(figures)
                                                       : figures;
    this.replace(replaceFigures, newFigures);
    return this;
    });
  },

  optimize$( figures ) {
    return map$( filter( figures,{ document: this }), figure => {
      const manager = this._resources || Config.GlobalResourceManager;
      return manager.optimizeFigures$([figure]) .then( newFigures => {
      figure.owner()._figures.replace(figure,newFigures[0]);
      });
    }).then( () => this );
  },

  // internals

  init( o = {} ) {

    this.addEvents( documentEvents );

    // this.label    => name
    // this.isStored => archived

    // Bind commit$ and revert$ to the document so they can be used directly in callbacks
    this._bind('commit$');
    this._bind('preview$');
    this._bind('revert$');
    this._bind('render$');

    this._lock = false;
    this._previewing = false;

    this._isSaved = false;

    this._dropPreviousHistoryOnNextCommit = false;

    // Used for syncronization, so views can fire change event
    this._activePageChanged = false;
    this._activeLayerChanged = false;

    if( this._pages.length === 0 ) {
      this._pages.set(new Page());
    }
    if( this.Properties !== o.Properties ) {
      // If we are not doing a clone, support direct figures and layers
      // setting (for one page documents)

      if( isArray(o.layers) ) {
        this.layers = o.layers;
      }
      if( isArray(o.figures) ) {
        this.figures = o.figures;
      }
    }

    this._activePage = this._pages[0];

    this.disableMarkUndo = 0;
    this.history = [ this._emptyPagesState() ];
    this._currentHistoryPosition = 0;
  },

  // Cross pages and layers add, remove and replace

  add( v ) {
    return this.activePage.activeLayer._figures.add(v);
  },

  remove( v ) {
    let e = [];
    if( isArray(v) ) {
      forEach( clone(v), figure => {
        e = e.concat( figure.layer.figures.remove(figure) );
      });
    }
    else {
      e = e.concat( v.layer.figures.remove(v) );
    }
    return e;
  },

  replace( o, n ) {
    const to = isArray(o) ? last(o) : o;
    const { layer } = to;
    const { activeLayer } = this;
    const i = layer === activeLayer ? layer.figures.indexOf(to) : activeLayer.figures.length ;
    const added = activeLayer._figures.insert(i,n);
    const e = this.remove(o);
    e.added = added;
    return e;
  },

  clear() {
    return this.releasePages();
  },

  forEachFigure( func ) {
    const { pages } = this;
    for( let k = 0, kEnd = pages.length; k < kEnd; ++k ) {
      const stop = pages[k].forEachFigure(func);
      if( stop ) {
        return false;
      }
    }
  },

  isSaved() {
    return this._isSaved;
  },

  _pagesState(  state ) {
    return state ? cloneDeep(state)
                 : cloneDeep(this._pages);
  },

  _emptyPagesState() {
    return [ new Page() ];
  },

  _applyUndoRedo( stateData, type) {
    const _this = this;
    _this.lock();
    const waitList = [];
    _this.fireEvent('historycommitstarted',_this,waitList,type);
    return whenAll(waitList).then( () => {

    // Check maintaining current active layer
    const api = _this.activePageIndex;
    const pages = _this._pagesState(stateData);
    _this.pushMarkUndoDisabled();
    // Try to keep the current active page, it will be good to store more information
    const page = _this._activePage = pages[ api < pages.length ? api : 0 ];
    _this._pages.set( pages );
    _this.fireEvent('activepagechanged',_this,page);

    return _this.commit$().then( () => {

    _this.fireEvent(type,_this);
    _this.popMarkUndoDisabled();

    }); });
  },

  vectorBounds_() {
    return Figures.vectorBounds_(this.activeLayerFigures);
  },
  bounds_() {
    return Figures.bounds_(this.activeLayerFigures);
  },
  vectorBounds$() {
    return Figures.vectorBounds$(this.activeLayerFigures);
  },
  bounds$() {
    return Figures.bounds$(this.activeLayerFigures);
  },

  extraXmlParsing(/* docNode */) {

  }
});

Document.fromXmlString = function(xmlString,bcFixes) {
  const domDoc = Xml.fromString(xmlString);
  return Document.fromXml(domDoc.documentElement,bcFixes);
};

Document.fromXml = function(node,bcFixes) {
  let Type = Document,
      xType = null;

  let p = {};

  // Support <Document><Pages>... and <Document><Layers>..., this will be cleaner in the future
  const docNode = Xml.child(node,'Document');
  const lMetricsNode = Xml.child(node, 'Metrics');

  let background = new DocumentBackground();

  if( docNode ) {
    const backgroundNode = Xml.child(docNode,'Background');
    if( backgroundNode ) {
      const brush = Brush.fromXml(backgroundNode),
            id = backgroundNode.getAttribute('Id');
      background = new DocumentBackground({ brush, id });
    }

    const pagesNode = Xml.child(docNode,'Pages');
    if( pagesNode ) {
      node = pagesNode;
    }
    else {
      const layersNode = Xml.child(docNode,'Layers');
      if( layersNode ) {
        node = layersNode;
      }
    }
    xType = docNode.getAttribute('XType');
    Type = _documentType[xType] || Document;

    p = Xml.parseProperties(docNode,Type);
  }

  p.pages = ( Type.PageType || Page ).arrayFromXml(node,bcFixes);
  p.background = background;
  const doc = new Type(p);
  if(docNode) {
    doc._metadata = Metadata.parseXml(docNode,doc);
  }
  if (lMetricsNode) {
    const lMetricsType = lMetricsNode.getAttribute('Unit');
    doc._metrics = lMetricsType;
    Magnitude.setDefaultType(lMetricsType);
  }
  else
  {    
    Magnitude.setDefaultType('mm');
  }
  doc.extraXmlParsing(docNode);

  return doc;
};

let _backwardCompatibleDecoding$ = Document.backwardCompatibleDecoding$ = function(func,cdl) {
  const bcFixes = [];
  const doc = func(cdl,bcFixes);
  return bcFixes.length > 0 ? map$( bcFixes, bpFix => bpFix() ).then( () => doc ) : resolve(doc);
};

Document.fromXmlString$ = function(xml) {
  return _backwardCompatibleDecoding$(Document.fromXmlString,xml);
};
Document.fromXml$ = function(xml) {
  return _backwardCompatibleDecoding$(Document.fromXml,xml);
};

Document.load$ = function(config,options) {
  return Config.GlobalResourceManager.loadDocument$(config,options);
};

Layers.load$ = function(config,options) {
  return Document.load$(config,options) .then( doc => doc.releaseLayers() );
};

Figures.load$ = function( docModel ) {
  return Layers.load$(docModel) .then( layers => {
  return reduce( layers, (all,l) => concat( all, l.release() ), [] ); // [TODO] flatMap
  });
};

Figures.fromXmlString = function( string ) {
  return Document.fromXmlString(string).releaseFigures();
};

const _documentType = {
  'System.Document': Document
};

export const registerDocumentType = function(xType,documentType) {
  _documentType[xType] = documentType;
};

export default Document;



/** WEBPACK FOOTER **
 ** ./src/cdl/document/document.js
 **/